import { ContributionRecord } from '../types';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useState } from 'react';

interface ContributionDataTableProps {
  records: ContributionRecord[];
  onSort: (field: string) => void;
  sortField: string;
  sortOrder: 'asc' | 'desc';
}

const ContributionDataTable = ({ records, onSort, sortField, sortOrder }: ContributionDataTableProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const getStatusBadge = (status: string) => {
    const styles = {
      verified: 'bg-success/10 text-success',
      pending: 'bg-warning/10 text-warning',
      rejected: 'bg-destructive/10 text-destructive',
    };

    return (
      <span className={`px-2 py-1 rounded-sm text-xs font-medium ${styles[status as keyof typeof styles]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const filteredRecords = records.filter(record => {
    const matchesSearch = record.memberName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         record.recordedBy.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || record.verificationStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (amount: number) => {
    return `${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} VUV`;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return <Icon name="ChevronsUpDown" size={16} className="text-muted-foreground" />;
    return <Icon name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-primary" />;
  };

  return (
    <div className="bg-card rounded-md border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Search by member or recorder..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="w-full md:w-48">
            <Select
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'verified', label: 'Verified' },
                { value: 'pending', label: 'Pending' },
                { value: 'rejected', label: 'Rejected' },
              ]}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="Filter by status"
            />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => onSort('date')}
                  className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-interactive"
                >
                  Date
                  <SortIcon field="date" />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <span className="text-sm font-medium text-foreground">Group Name</span>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => onSort('memberName')}
                  className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-interactive"
                >
                  Member
                  <SortIcon field="memberName" />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => onSort('amount')}
                  className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-interactive"
                >
                  Amount
                  <SortIcon field="amount" />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <span className="text-sm font-medium text-foreground">Recorded By</span>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => onSort('verificationStatus')}
                  className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-interactive"
                >
                  Status
                  <SortIcon field="verificationStatus" />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <span className="text-sm font-medium text-foreground">Notes</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredRecords.map((record) => (
              <tr key={record.id} className="hover:bg-muted/50 transition-interactive">
                <td className="px-6 py-4 text-sm text-foreground">
                  {formatDate(record.date)}
                </td>
                <td className="px-6 py-4 text-sm font-medium text-foreground">
                  {record.groupName || 'Unknown Group'}
                </td>
                <td className="px-6 py-4 text-sm font-medium text-foreground">
                  {record.memberName}
                </td>
                <td className="px-6 py-4 text-sm font-semibold text-foreground">
                  {formatCurrency(record.amount)}
                </td>
                <td className="px-6 py-4 text-sm text-muted-foreground">
                  {record.recordedBy}
                </td>
                <td className="px-6 py-4">
                  {getStatusBadge(record.verificationStatus)}
                </td>
                <td className="px-6 py-4 text-sm text-muted-foreground">
                  {record.notes || '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredRecords.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No records found matching your criteria</p>
        </div>
      )}

      <div className="p-4 border-t border-border flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing {filteredRecords.length} of {records.length} records
        </p>
      </div>
    </div>
  );
};

export default ContributionDataTable;